import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  Download,
  Upload,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Search,
  Baby,
  ListChecks,
  Clock
} from 'lucide-react';
import api from '../api';

const TRIMESTERS = [
  { id: 1, name: 'First Trimester', weeks: '1-12', color: 'from-pink-500 to-rose-500', bgColor: 'bg-pink-50', borderColor: 'border-pink-200' },
  { id: 2, name: 'Second Trimester', weeks: '13-26', color: 'from-purple-500 to-indigo-500', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
  { id: 3, name: 'Third Trimester', weeks: '27-42', color: 'from-blue-500 to-cyan-500', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
];

const AdminPregnancyTasks = () => {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTrimesters, setExpandedTrimesters] = useState({ 1: true, 2: true, 3: true });
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [editingWeek, setEditingWeek] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [newWeek, setNewWeek] = useState(null);
  const [newTask, setNewTask] = useState(null);
  const [notification, setNotification] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTrimester, setFilterTrimester] = useState('all');

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/children/pregnancy-weeks/');
      setWeeks(response.data);
    } catch (error) {
      showNotification('Error loading pregnancy weeks', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Export functionality
  const handleExport = async () => {
    try {
      const response = await api.get('/children/pregnancy-weeks/export/');
      const exportData = {
        version: '1.0',
        exportDate: new Date().toISOString(),
        weeks: response.data
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pregnancy-tasks-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showNotification('Pregnancy tasks exported successfully');
    } catch (error) {
      showNotification('Error exporting tasks', 'error');
    }
  };

  // Import functionality
  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const weeksData = data.weeks || data;

      await api.post('/children/pregnancy-weeks/import/', weeksData);
      showNotification('Pregnancy tasks imported successfully');
      fetchData();
    } catch (error) {
      showNotification(`Error importing file: ${error.message}`, 'error');
    }

    event.target.value = '';
  };

  // Clear all functionality
  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to delete all pregnancy weeks and tasks? This action cannot be undone.')) return;

    try {
      await api.post('/children/pregnancy-weeks/clear_all/');
      showNotification('All pregnancy data has been deleted');
      fetchData();
    } catch (error) {
      showNotification('Error clearing data', 'error');
    }
  };

  // Week CRUD
  const handleAddWeek = () => {
    const existingWeekNumbers = weeks.map(w => w.week_number);
    const availableWeeks = Array.from({ length: 42 }, (_, i) => i + 1).filter(n => !existingWeekNumbers.includes(n));
    
    if (availableWeeks.length === 0) {
      showNotification('All 42 weeks have been created', 'error');
      return;
    }

    setNewWeek({
      week_number: availableWeeks[0],
      description: '',
      is_active: true
    });
    setEditingWeek(null);
    setNewTask(null);
    setEditingTask(null);
  };

  const handleSaveNewWeek = async () => {
    if (!newWeek.week_number) {
      showNotification('Please select a week number', 'error');
      return;
    }

    try {
      const response = await api.post('/children/pregnancy-weeks/', newWeek);
      setWeeks(prev => [...prev, response.data].sort((a, b) => a.week_number - b.week_number));
      setNewWeek(null);
      showNotification('Week added successfully');
    } catch (error) {
      showNotification('Error adding week', 'error');
    }
  };

  const handleEditWeek = (week) => {
    setEditingWeek({ ...week });
    setNewWeek(null);
    setNewTask(null);
    setEditingTask(null);
  };

  const handleUpdateWeek = async () => {
    try {
      const response = await api.patch(`/children/pregnancy-weeks/${editingWeek.id}/`, {
        description: editingWeek.description,
        is_active: editingWeek.is_active
      });
      setWeeks(prev => prev.map(w => w.id === editingWeek.id ? response.data : w));
      setEditingWeek(null);
      showNotification('Week updated successfully');
    } catch (error) {
      showNotification('Error updating week', 'error');
    }
  };

  const handleDeleteWeek = async (weekId) => {
    if (!window.confirm('Delete this week and all its tasks?')) return;

    try {
      await api.delete(`/children/pregnancy-weeks/${weekId}/`);
      setWeeks(prev => prev.filter(w => w.id !== weekId));
      showNotification('Week deleted successfully');
    } catch (error) {
      showNotification('Error deleting week', 'error');
    }
  };

  // Task CRUD
  const handleAddTask = (weekId) => {
    const week = weeks.find(w => w.id === weekId);
    setNewTask({
      week: weekId,
      statement: '',
      order: week?.tasks?.length || 0,
      is_active: true
    });
    setEditingTask(null);
    setNewWeek(null);
    setEditingWeek(null);
  };

  const handleSaveNewTask = async () => {
    if (!newTask.statement.trim()) {
      showNotification('Please enter a task statement', 'error');
      return;
    }

    try {
      const response = await api.post('/children/pregnancy-tasks/', newTask);
      setWeeks(prev => prev.map(w => {
        if (w.id === newTask.week) {
          return {
            ...w,
            tasks: [...(w.tasks || []), response.data]
          };
        }
        return w;
      }));
      setNewTask(null);
      showNotification('Task added successfully');
    } catch (error) {
      showNotification('Error adding task', 'error');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask({ ...task });
    setNewTask(null);
    setNewWeek(null);
    setEditingWeek(null);
  };

  const handleUpdateTask = async () => {
    if (!editingTask.statement.trim()) {
      showNotification('Please enter a task statement', 'error');
      return;
    }

    try {
      const response = await api.patch(`/children/pregnancy-tasks/${editingTask.id}/`, {
        statement: editingTask.statement,
        order: editingTask.order,
        is_active: editingTask.is_active
      });
      setWeeks(prev => prev.map(w => ({
        ...w,
        tasks: w.tasks?.map(t => t.id === editingTask.id ? response.data : t)
      })));
      setEditingTask(null);
      showNotification('Task updated successfully');
    } catch (error) {
      showNotification('Error updating task', 'error');
    }
  };

  const handleDeleteTask = async (taskId, weekId) => {
    if (!window.confirm('Delete this task?')) return;

    try {
      await api.delete(`/children/pregnancy-tasks/${taskId}/`);
      setWeeks(prev => prev.map(w => {
        if (w.id === weekId) {
          return {
            ...w,
            tasks: w.tasks?.filter(t => t.id !== taskId)
          };
        }
        return w;
      }));
      showNotification('Task deleted successfully');
    } catch (error) {
      showNotification('Error deleting task', 'error');
    }
  };

  // Bulk create tasks for a week
  const handleBulkAddTasks = async (weekId) => {
    const tasksInput = window.prompt('Enter tasks separated by new lines:');
    if (!tasksInput) return;

    const tasks = tasksInput.split('\n').filter(t => t.trim()).map((statement, index) => ({
      statement: statement.trim(),
      order: index,
      is_active: true
    }));

    try {
      await api.post('/children/pregnancy-tasks/bulk-create/', {
        week_id: weekId,
        tasks
      });
      showNotification(`${tasks.length} tasks added successfully`);
      fetchData();
    } catch (error) {
      showNotification('Error adding tasks', 'error');
    }
  };

  // Get available week numbers for dropdown
  const getAvailableWeekNumbers = () => {
    const existingWeekNumbers = weeks.map(w => w.week_number);
    return Array.from({ length: 42 }, (_, i) => i + 1).filter(n => !existingWeekNumbers.includes(n));
  };

  // Filter weeks
  const getFilteredWeeks = (trimesterId) => {
    return weeks.filter(week => {
      const matchesTrimester = week.trimester === trimesterId;
      const matchesSearch = searchTerm === '' || 
        week.week_number.toString().includes(searchTerm) ||
        week.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        week.tasks?.some(t => t.statement.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesFilter = filterTrimester === 'all' || week.trimester === parseInt(filterTrimester);
      
      return matchesTrimester && matchesSearch && matchesFilter;
    });
  };

  // Stats
  const totalWeeks = weeks.length;
  const totalTasks = weeks.reduce((sum, w) => sum + (w.tasks?.length || 0), 0);
  const activeWeeks = weeks.filter(w => w.is_active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#1e3a5f]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 p-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-xl shadow-2xl animate-pulse ${
          notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white font-semibold flex items-center gap-2`}>
          {notification.type === 'error' ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
          {notification.message}
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] bg-clip-text text-transparent flex items-center gap-3">
                <Baby className="w-10 h-10 text-[#1e3a5f]" />
                Pregnancy Task Manager
              </h1>
              <p className="text-gray-600 mt-2">Manage weekly tasks for pregnancy tracking</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleAddWeek}
                className="px-5 py-3 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Plus size={20} />
                Add Week
              </button>
              <button
                onClick={handleExport}
                className="px-5 py-3 bg-gray-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Download size={20} />
                Export
              </button>
              <button
                onClick={() => document.getElementById('import-file').click()}
                className="px-5 py-3 bg-gray-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Upload size={20} />
                Import
              </button>
              <input
                type="file"
                id="import-file"
                className="hidden"
                onChange={handleImport}
                accept=".json"
              />
              <button
                onClick={handleClearAll}
                className="px-5 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-all flex items-center gap-2"
              >
                <Trash2 size={20} />
                Clear All
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-r from-pink-100 to-rose-100 rounded-xl p-4 flex items-center gap-4">
              <div className="bg-white p-3 rounded-lg shadow">
                <Calendar className="w-6 h-6 text-pink-500" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Weeks</p>
                <p className="text-2xl font-bold text-gray-800">{totalWeeks} / 42</p>
              </div>
            </div>
            <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl p-4 flex items-center gap-4">
              <div className="bg-white p-3 rounded-lg shadow">
                <ListChecks className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Tasks</p>
                <p className="text-2xl font-bold text-gray-800">{totalTasks}</p>
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-100 to-cyan-100 rounded-xl p-4 flex items-center gap-4">
              <div className="bg-white p-3 rounded-lg shadow">
                <Clock className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Weeks</p>
                <p className="text-2xl font-bold text-gray-800">{activeWeeks}</p>
              </div>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search weeks or tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
              />
            </div>
            <select
              value={filterTrimester}
              onChange={(e) => setFilterTrimester(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
            >
              <option value="all">All Trimesters</option>
              <option value="1">First Trimester (1-12)</option>
              <option value="2">Second Trimester (13-26)</option>
              <option value="3">Third Trimester (27-42)</option>
            </select>
          </div>
        </div>

        {/* New Week Form */}
        {newWeek && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-2 border-green-200">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-500" />
              Add New Week
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Week Number</label>
                <select
                  value={newWeek.week_number}
                  onChange={(e) => setNewWeek({ ...newWeek, week_number: parseInt(e.target.value) })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {getAvailableWeekNumbers().map(num => (
                    <option key={num} value={num}>Week {num}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                <input
                  type="text"
                  value={newWeek.description}
                  onChange={(e) => setNewWeek({ ...newWeek, description: e.target.value })}
                  placeholder="e.g., Baby's heart begins to beat"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newWeek.is_active}
                  onChange={(e) => setNewWeek({ ...newWeek, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-600">Active</span>
              </label>
              <div className="flex-1"></div>
              <button
                onClick={handleSaveNewWeek}
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Save size={18} />
                Save Week
              </button>
              <button
                onClick={() => setNewWeek(null)}
                className="px-5 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
              >
                <X size={18} />
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Trimesters */}
        <div className="space-y-6">
          {TRIMESTERS.map(trimester => {
            const trimesterWeeks = getFilteredWeeks(trimester.id);
            const isExpanded = expandedTrimesters[trimester.id];

            if (filterTrimester !== 'all' && parseInt(filterTrimester) !== trimester.id) {
              return null;
            }

            return (
              <div key={trimester.id} className="bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Trimester Header */}
                <div
                  className={`bg-gradient-to-r ${trimester.color} p-5 cursor-pointer`}
                  onClick={() => setExpandedTrimesters(prev => ({ ...prev, [trimester.id]: !prev[trimester.id] }))}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-6 h-6 text-white" />
                      ) : (
                        <ChevronRight className="w-6 h-6 text-white" />
                      )}
                      <div>
                        <h2 className="text-xl font-bold text-white">{trimester.name}</h2>
                        <p className="text-white/80 text-sm">Weeks {trimester.weeks}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="bg-white/20 px-3 py-1 rounded-full text-white text-sm font-medium">
                        {trimesterWeeks.length} weeks
                      </span>
                      <span className="bg-white/20 px-3 py-1 rounded-full text-white text-sm font-medium">
                        {trimesterWeeks.reduce((sum, w) => sum + (w.tasks?.length || 0), 0)} tasks
                      </span>
                    </div>
                  </div>
                </div>

                {/* Trimester Content */}
                {isExpanded && (
                  <div className={`p-5 ${trimester.bgColor}`}>
                    {trimesterWeeks.length > 0 ? (
                      <div className="space-y-4">
                        {trimesterWeeks.map(week => (
                          <WeekCard
                            key={week.id}
                            week={week}
                            trimester={trimester}
                            expanded={expandedWeeks[week.id]}
                            onToggle={() => setExpandedWeeks(prev => ({ ...prev, [week.id]: !prev[week.id] }))}
                            editingWeek={editingWeek}
                            setEditingWeek={setEditingWeek}
                            onEditWeek={() => handleEditWeek(week)}
                            onUpdateWeek={handleUpdateWeek}
                            onDeleteWeek={() => handleDeleteWeek(week.id)}
                            onAddTask={() => handleAddTask(week.id)}
                            onBulkAddTasks={() => handleBulkAddTasks(week.id)}
                            newTask={newTask}
                            setNewTask={setNewTask}
                            onSaveNewTask={handleSaveNewTask}
                            editingTask={editingTask}
                            setEditingTask={setEditingTask}
                            onEditTask={handleEditTask}
                            onUpdateTask={handleUpdateTask}
                            onDeleteTask={(taskId) => handleDeleteTask(taskId, week.id)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No weeks added for this trimester yet.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {weeks.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <Baby className="w-24 h-24 mx-auto text-gray-300 mb-4" />
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No Pregnancy Weeks Added</h3>
            <p className="text-gray-500 mb-6">Start by creating your first pregnancy week with tasks</p>
            <button
              onClick={handleAddWeek}
              className="px-6 py-3 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] text-white rounded-xl font-semibold hover:shadow-lg transition-all inline-flex items-center gap-2"
            >
              <Plus size={20} />
              Add First Week
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Week Card Component
const WeekCard = ({
  week,
  trimester,
  expanded,
  onToggle,
  editingWeek,
  setEditingWeek,
  onEditWeek,
  onUpdateWeek,
  onDeleteWeek,
  onAddTask,
  onBulkAddTasks,
  newTask,
  setNewTask,
  onSaveNewTask,
  editingTask,
  setEditingTask,
  onEditTask,
  onUpdateTask,
  onDeleteTask
}) => {
  const isEditingThisWeek = editingWeek?.id === week.id;

  return (
    <div className={`bg-white rounded-xl border-2 ${trimester.borderColor} overflow-hidden ${!week.is_active ? 'opacity-60' : ''}`}>
      {/* Week Header */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {expanded ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 bg-gradient-to-r ${trimester.color} text-white text-sm font-bold rounded-full`}>
                  Week {week.week_number}
                </span>
                {!week.is_active && (
                  <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
                    Inactive
                  </span>
                )}
              </div>
              {week.description && (
                <p className="text-gray-600 text-sm mt-1">{week.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <span className="text-sm text-gray-500">{week.tasks?.length || 0} tasks</span>
            <button
              onClick={onEditWeek}
              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={onDeleteWeek}
              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Week Edit Form */}
      {isEditingThisWeek && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="bg-blue-50 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-gray-700 mb-3">Edit Week {week.week_number}</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={editingWeek.description || ''}
                  onChange={(e) => setEditingWeek({ ...editingWeek, description: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingWeek.is_active}
                    onChange={(e) => setEditingWeek({ ...editingWeek, is_active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-600">Active</span>
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={onUpdateWeek}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                  >
                    <Save size={16} />
                    Save
                  </button>
                  <button
                    onClick={() => setEditingWeek(null)}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-1"
                  >
                    <X size={16} />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Week Content (Tasks) */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Add Task Buttons */}
          <div className="flex gap-2 my-4">
            <button
              onClick={onAddTask}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm"
            >
              <Plus size={16} />
              Add Task
            </button>
            <button
              onClick={onBulkAddTasks}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 text-sm"
            >
              <ListChecks size={16} />
              Bulk Add
            </button>
          </div>

          {/* New Task Form */}
          {newTask?.week === week.id && (
            <div className="bg-green-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-3">Add New Task</h4>
              <textarea
                value={newTask.statement}
                onChange={(e) => setNewTask({ ...newTask, statement: e.target.value })}
                placeholder="Enter task statement..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[80px]"
                autoFocus
              />
              <div className="flex items-center justify-between mt-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newTask.is_active}
                    onChange={(e) => setNewTask({ ...newTask, is_active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-600">Active</span>
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={onSaveNewTask}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                  >
                    <Save size={16} />
                    Save
                  </button>
                  <button
                    onClick={() => setNewTask(null)}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-1"
                  >
                    <X size={16} />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tasks List */}
          {week.tasks && week.tasks.length > 0 ? (
            <div className="space-y-2">
              {week.tasks.map((task, index) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  index={index}
                  editingTask={editingTask}
                  setEditingTask={setEditingTask}
                  onEditTask={() => onEditTask(task)}
                  onUpdateTask={onUpdateTask}
                  onDeleteTask={() => onDeleteTask(task.id)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <ListChecks className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No tasks added for this week yet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Task Item Component
const TaskItem = ({
  task,
  index,
  editingTask,
  setEditingTask,
  onEditTask,
  onUpdateTask,
  onDeleteTask
}) => {
  const isEditing = editingTask?.id === task.id;

  if (isEditing) {
    return (
      <div className="bg-blue-50 rounded-lg p-4">
        <textarea
          value={editingTask.statement}
          onChange={(e) => setEditingTask({ ...editingTask, statement: e.target.value })}
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
          autoFocus
        />
        <div className="flex items-center justify-between mt-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={editingTask.is_active}
              onChange={(e) => setEditingTask({ ...editingTask, is_active: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-sm text-gray-600">Active</span>
          </label>
          <div className="flex gap-2">
            <button
              onClick={onUpdateTask}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
            >
              <Save size={16} />
              Save
            </button>
            <button
              onClick={() => setEditingTask(null)}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-1"
            >
              <X size={16} />
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors ${!task.is_active ? 'opacity-50' : ''}`}>
      <span className="flex-shrink-0 w-6 h-6 bg-gray-200 text-gray-600 text-xs font-bold rounded-full flex items-center justify-center mt-0.5">
        {index + 1}
      </span>
      <div className="flex-1">
        <p className="text-gray-800">{task.statement}</p>
        {!task.is_active && (
          <span className="inline-block mt-1 px-2 py-0.5 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
            Inactive
          </span>
        )}
      </div>
      <div className="flex gap-1">
        <button
          onClick={onEditTask}
          className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={onDeleteTask}
          className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AdminPregnancyTasks;